// "+ New RFQ" modal — the primary path for typing in an RFQ that landed in
// the broker's personal email. Combines customer (existing or new) +
// contact (existing, new, or none) + line items in one form. Server-side
// branching is in persistManualRfq; this component only assembles and
// submits the payload.
//
// Contact-defaulting rule (deliberate UX choice, not a bug):
//   - 0 contacts on file → force "New contact" mode (RFQ needs one for GHL)
//   - 1 contact on file → auto-select it
//   - 2+ contacts on file → no default; broker must pick consciously
// Silent defaults on multi-contact customers risk routing quotes to the
// wrong procurement person at the same firm.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, Trash2 } from 'lucide-react';
import {
  api,
  type CertType,
  type ConditionCode,
  type CreateRfqRequest,
  type CreateRfqResponse,
  type CustomerListItem,
  type CustomerScorecardResponse,
  type ListCustomersResponse,
  type ManualRfqContact,
  type ManualRfqCustomer,
  type ManualRfqLineItem,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';
import { parseBulkPaste } from '@shared/bulk-paste-parser';
import { phoneCountry, formatPhoneDisplay } from '@/lib/phone-country';
import { fmtCompactUsd } from '@/lib/format';

interface Props {
  open: boolean;
  onClose: () => void;
  /** When opened from a customer detail view, prefills the customer picker. */
  presetCustomer?: { id: string; legalName: string } | null;
}

type CustomerMode = 'select' | 'create';
type ContactMode = 'select' | 'create' | 'none';

interface LineItemDraft {
  partNumber: string;
  altPartNumber: string;
  description: string;
  qty: string;
  conditionRequested: ConditionCode | '';
  certRequested: CertType | '';
  isAog: boolean;
  notes: string;
}

const CONDITION_OPTIONS: Array<{ value: ConditionCode; label: string }> = [
  { value: 'NE', label: 'NE — New' },
  { value: 'NS', label: 'NS — New Surplus' },
  { value: 'FN', label: 'FN — Factory New' },
  { value: 'OH', label: 'OH — Overhauled' },
  { value: 'SV', label: 'SV — Serviceable' },
  { value: 'AR', label: 'AR — As Removed' },
  { value: 'RP', label: 'RP — Repairable' },
];

const CERT_OPTIONS: Array<{ value: CertType; label: string }> = [
  { value: 'faa_8130_3', label: 'FAA 8130-3' },
  { value: 'easa_form_1', label: 'EASA Form 1' },
  { value: 'dual_release', label: 'Dual release (8130 + EASA)' },
  { value: 'coc', label: 'OEM CofC' },
  { value: 'none', label: 'None / trace-only' },
];

function emptyLine(): LineItemDraft {
  return {
    partNumber: '',
    altPartNumber: '',
    description: '',
    qty: '1',
    conditionRequested: '',
    certRequested: '',
    isAog: false,
    notes: '',
  };
}

export function NewRfqModal({ open, onClose, presetCustomer }: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();

  // ---------------- Customer ----------------
  const [customerMode, setCustomerMode] = useState<CustomerMode>('select');
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    legalName: string;
  } | null>(null);

  const [newCustomerLegalName, setNewCustomerLegalName] = useState('');
  const [newCustomerDisplayName, setNewCustomerDisplayName] = useState('');
  const [newCustomerEmailDomain, setNewCustomerEmailDomain] = useState('');
  const [newCustomerCountryCode, setNewCustomerCountryCode] = useState('');

  // ---------------- Contact ----------------
  const [contactMode, setContactMode] = useState<ContactMode>('create');
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactFullName, setNewContactFullName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactJobTitle, setNewContactJobTitle] = useState('');

  // ---------------- RFQ + lines ----------------
  const [isAog, setIsAog] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemDraft[]>([emptyLine()]);
  // Bulk-paste affordance: textarea + a one-click parse that appends parsed
  // lines (or replaces the initial blank if the broker hasn't typed anything
  // yet). Useful for the "customer sent 30+ PNs" case — typing them one at a
  // time is the bottleneck this kills.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);

  // Reset everything on close so reopening is clean.
  useEffect(() => {
    if (!open) {
      setCustomerMode('select');
      setCustomerSearch('');
      setSelectedCustomer(null);
      setNewCustomerLegalName('');
      setNewCustomerDisplayName('');
      setNewCustomerEmailDomain('');
      setNewCustomerCountryCode('');
      setContactMode('create');
      setSelectedContactId('');
      setNewContactEmail('');
      setNewContactFullName('');
      setNewContactPhone('');
      setNewContactJobTitle('');
      setIsAog(false);
      setNotes('');
      setLineItems([emptyLine()]);
      setBulkOpen(false);
      setBulkText('');
      setBulkError(null);
      setBulkNotice(null);
    } else if (presetCustomer) {
      setCustomerMode('select');
      setSelectedCustomer(presetCustomer);
    }
  }, [open, presetCustomer]);

  // ---------------- Customer search ----------------
  const customerSearchQuery = useQuery({
    queryKey: ['customers', { q: customerSearch }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (customerSearch) params.set('q', customerSearch);
      params.set('limit', '20');
      return api<ListCustomersResponse>(`/api/customers?${params}`, {
        tokenGetter: () => getToken(),
      });
    },
    enabled:
      open && customerMode === 'select' && !selectedCustomer && customerSearch.length > 0,
    staleTime: 5_000,
  });

  // ---------------- Contacts for selected customer ----------------
  const customerScorecardQuery = useQuery({
    queryKey: ['customer', selectedCustomer?.id],
    queryFn: () =>
      api<CustomerScorecardResponse>(`/api/customers/${selectedCustomer!.id}`, {
        tokenGetter: () => getToken(),
      }),
    enabled: open && customerMode === 'select' && !!selectedCustomer,
    staleTime: 5_000,
  });

  // Memoize so the effect below doesn't rerun (and reset the broker's
  // contact selection) every render. Without this, `?? []` creates a fresh
  // array reference on each render whenever data is undefined.
  const existingContacts = useMemo(
    () => customerScorecardQuery.data?.contacts ?? [],
    [customerScorecardQuery.data?.contacts],
  );

  // Apply the contact-defaulting rule whenever the selected customer's
  // contact list resolves. Depend on stable primitives (id + length + first
  // contact id) rather than the array reference to avoid resets on refetch.
  useEffect(() => {
    if (!selectedCustomer || customerScorecardQuery.isLoading) return;
    if (existingContacts.length === 0) {
      setContactMode('create');
      setSelectedContactId('');
    } else if (existingContacts.length === 1) {
      setContactMode('select');
      setSelectedContactId(existingContacts[0]!.id);
    } else {
      setContactMode('select');
      setSelectedContactId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCustomer?.id,
    customerScorecardQuery.isLoading,
    existingContacts.length,
    existingContacts[0]?.id,
  ]);

  // For "create new customer" mode there are no existing contacts, so always
  // force create-contact mode and clear any selection.
  useEffect(() => {
    if (customerMode === 'create') {
      setContactMode('create');
      setSelectedContactId('');
    }
  }, [customerMode]);

  // ---------------- Submit ----------------
  const createMutation = useMutation({
    mutationFn: async () => {
      let customerPayload: ManualRfqCustomer;
      if (customerMode === 'select' && selectedCustomer) {
        customerPayload = { kind: 'existing', publicId: selectedCustomer.id };
      } else {
        customerPayload = {
          kind: 'new',
          legalName: newCustomerLegalName.trim(),
        };
        if (newCustomerDisplayName.trim()) {
          customerPayload.displayName = newCustomerDisplayName.trim();
        }
        if (newCustomerEmailDomain.trim()) {
          customerPayload.emailDomain = newCustomerEmailDomain.trim().toLowerCase();
        }
        if (newCustomerCountryCode.trim()) {
          customerPayload.countryCode = newCustomerCountryCode.trim().toUpperCase();
        }
      }

      let contactPayload: ManualRfqContact;
      if (contactMode === 'none') {
        contactPayload = { kind: 'none' };
      } else if (contactMode === 'select') {
        contactPayload = { kind: 'existing', publicId: selectedContactId };
      } else {
        contactPayload = { kind: 'new', email: newContactEmail.trim() };
        if (newContactFullName.trim()) contactPayload.fullName = newContactFullName.trim();
        if (newContactPhone.trim()) contactPayload.phone = newContactPhone.trim();
        if (newContactJobTitle.trim()) contactPayload.jobTitle = newContactJobTitle.trim();
      }

      const lineItemsPayload: ManualRfqLineItem[] = lineItems.map((li) => {
        // canSubmit gates `Number(li.qty) > 0` before we get here — pass the
        // parsed number through honestly rather than masking with a default.
        const out: ManualRfqLineItem = {
          partNumber: li.partNumber.trim(),
          qty: Number(li.qty),
        };
        if (li.altPartNumber.trim()) out.altPartNumber = li.altPartNumber.trim();
        if (li.description.trim()) out.description = li.description.trim();
        if (li.conditionRequested) out.conditionRequested = li.conditionRequested;
        if (li.certRequested) out.certRequested = li.certRequested;
        if (li.isAog) out.isAog = true;
        if (li.notes.trim()) out.notes = li.notes.trim();
        return out;
      });

      const body: CreateRfqRequest = {
        customer: customerPayload,
        contact: contactPayload,
        rfq: {
          isAog,
          isUrgent: isUrgent && !isAog,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        lineItems: lineItemsPayload,
      };

      return api<CreateRfqResponse>('/api/rfqs', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customer', data.customer.id] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
      // Send the broker straight to the new RFQ so they can start sourcing.
      setLocation(`/rfqs/${data.rfq.id}`);
      onClose();
    },
  });

  // ---------------- Validation gate ----------------
  const canSubmit = useMemo(() => {
    if (createMutation.isPending) return false;

    // Customer
    if (customerMode === 'select') {
      if (!selectedCustomer) return false;
    } else if (!newCustomerLegalName.trim()) {
      return false;
    }

    // Contact
    if (contactMode === 'select') {
      if (!selectedContactId) return false;
    } else if (contactMode === 'create') {
      if (!newContactEmail.trim()) return false;
    }

    // Lines — at least one with a part number and positive qty
    const okLines = lineItems.every(
      (li) => li.partNumber.trim().length > 0 && Number(li.qty) > 0,
    );
    if (!okLines) return false;

    return true;
  }, [
    createMutation.isPending,
    customerMode,
    selectedCustomer,
    newCustomerLegalName,
    contactMode,
    selectedContactId,
    newContactEmail,
    lineItems,
  ]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-3xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold">New RFQ</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-sm"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto">
          {/* ===== Customer ===== */}
          <section>
            <SectionHeader
              title="Customer"
              right={
                <ToggleGroup
                  value={customerMode}
                  onChange={(v) => {
                    setCustomerMode(v);
                    if (v === 'create') setSelectedCustomer(null);
                  }}
                  options={[
                    { value: 'select', label: 'Existing' },
                    { value: 'create', label: 'New' },
                  ]}
                />
              }
            />

            {customerMode === 'select' ? (
              selectedCustomer ? (
                <div className="flex items-center justify-between gap-3 px-3 py-2 border border-border rounded bg-surface-muted/40">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {selectedCustomer.legalName}
                    </div>
                    <div className="text-[11px] text-foreground-muted font-mono">
                      {selectedCustomer.id}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedCustomer(null);
                      setSelectedContactId('');
                    }}
                    className="text-xs text-foreground-muted hover:text-foreground"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    autoFocus
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search by name, domain, country, RFQ ref…"
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                  {customerSearch.length > 0 && (
                    <div className="mt-2 max-h-80 overflow-auto border border-border rounded">
                      {customerSearchQuery.isLoading && (
                        <div className="px-3 py-2 text-xs text-foreground-muted">Searching…</div>
                      )}
                      {!customerSearchQuery.isLoading &&
                        (customerSearchQuery.data?.customers ?? []).length === 0 && (
                          <div className="px-3 py-2 text-xs text-foreground-muted italic">
                            No matches. Switch to "New" to create one.
                          </div>
                        )}
                      {(customerSearchQuery.data?.customers ?? []).map((c: CustomerListItem) => (
                        <CustomerResultRow
                          key={c.id}
                          c={c}
                          onPick={() =>
                            setSelectedCustomer({
                              id: c.id,
                              legalName: c.displayName ?? c.legalName,
                            })
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="space-y-3">
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Legal name *
                  </div>
                  <input
                    value={newCustomerLegalName}
                    onChange={(e) => setNewCustomerLegalName(e.target.value)}
                    placeholder="SFL General Trading LLC"
                    maxLength={200}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground mb-1">
                      Email domain
                    </div>
                    <input
                      value={newCustomerEmailDomain}
                      onChange={(e) => setNewCustomerEmailDomain(e.target.value)}
                      placeholder="sflgt.com"
                      maxLength={253}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground mb-1">
                      Country (ISO 2)
                    </div>
                    <input
                      value={newCustomerCountryCode}
                      onChange={(e) =>
                        setNewCustomerCountryCode(e.target.value.toUpperCase().slice(0, 2))
                      }
                      placeholder="AE"
                      maxLength={2}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                    />
                  </label>
                </div>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Display name (optional)
                  </div>
                  <input
                    value={newCustomerDisplayName}
                    onChange={(e) => setNewCustomerDisplayName(e.target.value)}
                    placeholder="Defaults to legal name"
                    maxLength={200}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
              </div>
            )}
          </section>

          {/* ===== Contact ===== */}
          <section>
            <SectionHeader
              title="Contact"
              right={
                customerMode === 'select' && selectedCustomer ? (
                  <ToggleGroup
                    value={contactMode}
                    onChange={(v) => setContactMode(v)}
                    options={
                      existingContacts.length > 0
                        ? [
                            { value: 'select', label: 'Existing' },
                            { value: 'create', label: 'New' },
                            { value: 'none', label: 'None' },
                          ]
                        : [
                            { value: 'create', label: 'New' },
                            { value: 'none', label: 'None' },
                          ]
                    }
                  />
                ) : null
              }
            />

            {contactMode === 'none' ? (
              <div className="text-xs text-foreground-muted italic">
                No contact will be linked. The RFQ will not push to GHL.
              </div>
            ) : contactMode === 'select' ? (
              customerScorecardQuery.isLoading ? (
                <div className="text-xs text-foreground-muted">Loading contacts…</div>
              ) : existingContacts.length === 0 ? (
                <div className="text-xs text-foreground-muted italic">
                  No contacts on file — switch to "New".
                </div>
              ) : (
                <select
                  value={selectedContactId}
                  onChange={(e) => setSelectedContactId(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                >
                  {existingContacts.length > 1 && (
                    <option value="">Select contact…</option>
                  )}
                  {existingContacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.fullName ?? c.email}
                      {c.jobTitle ? ` · ${c.jobTitle}` : ''} ({c.email})
                    </option>
                  ))}
                </select>
              )
            ) : (
              <div className="space-y-3">
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Email *</div>
                  <input
                    type="email"
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                    placeholder="najdi@sflgt.com"
                    maxLength={254}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground mb-1">Full name</div>
                    <input
                      value={newContactFullName}
                      onChange={(e) => setNewContactFullName(e.target.value)}
                      maxLength={200}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground mb-1">Job title</div>
                    <input
                      value={newContactJobTitle}
                      onChange={(e) => setNewContactJobTitle(e.target.value)}
                      maxLength={120}
                      placeholder="Procurement"
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                    />
                  </label>
                </div>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Phone</div>
                  <input
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value)}
                    maxLength={40}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
              </div>
            )}
          </section>

          {/* ===== RFQ details ===== */}
          <section>
            <SectionHeader title="RFQ details" />
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isAog}
                  onChange={(e) => setIsAog(e.target.checked)}
                />
                AOG (aircraft on ground — urgent)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isUrgent}
                  disabled={isAog}
                  onChange={(e) => setIsUrgent(e.target.checked)}
                />
                Urgent (ASAP / expedite — below AOG)
              </label>
              <label className="block">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  Notes (optional)
                </div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  maxLength={5000}
                  placeholder="Anything about the inquiry, deadlines, end-use, etc."
                  className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                />
              </label>
            </div>
          </section>

          {/* ===== Line items ===== */}
          <section>
            <SectionHeader
              title={`Line items (${lineItems.length})`}
              right={
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setBulkOpen((v) => !v);
                      setBulkError(null);
                      setBulkNotice(null);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    {bulkOpen ? 'Close bulk paste' : 'Bulk paste'}
                  </button>
                  <button
                    onClick={() => setLineItems([...lineItems, emptyLine()])}
                    className="text-xs flex items-center gap-1 text-primary hover:underline"
                  >
                    <Plus className="w-3 h-3" /> Add line
                  </button>
                </div>
              }
            />

            {bulkOpen && (
              <div className="mb-3 border border-border rounded p-3 bg-surface-muted/40 space-y-2">
                <div className="text-xs text-foreground-muted">
                  Paste one P/N per line. Optional comma- or tab-separated qty and description.
                  Blank lines and <span className="font-mono">#</span> comments are ignored.
                </div>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={6}
                  placeholder={'RS1267-5\n4313500-00-6600, 2\n798457-2, 1, Flight deck temp selector'}
                  className="w-full px-2 py-1.5 text-sm font-mono bg-background border border-border rounded resize-y"
                  autoFocus
                />
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs">
                    {bulkError && <span className="text-destructive">{bulkError}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setBulkError(null);
                      setBulkNotice(null);
                      // Cap respects how many slots remain under the 100-line
                      // server limit (existing non-empty + parsed ≤ 100). The
                      // lone empty starter line is REPLACED on success so it
                      // shouldn't count against the cap; everything else does.
                      const isLoneBlank = lineItems.length === 1 && !lineItems[0]?.partNumber.trim();
                      const existingCount = isLoneBlank
                        ? 0
                        : lineItems.filter((li) => li.partNumber.trim().length > 0).length;
                      const remaining = Math.max(0, 100 - existingCount);
                      if (remaining === 0) {
                        setBulkError('This RFQ already has 100 lines (the server cap). Remove some lines or split into a follow-up RFQ.');
                        return;
                      }
                      const result = parseBulkPaste(bulkText, remaining);
                      if (result.rows.length === 0) {
                        setBulkError('No part numbers detected — check the format.');
                        return;
                      }
                      // BulkPastedRow → LineItemDraft (defaults from emptyLine
                      // for the fields the paste doesn't set — condition, etc.).
                      const parsed: LineItemDraft[] = result.rows.map((r) => ({
                        ...emptyLine(),
                        partNumber: r.partNumber,
                        qty: r.qty,
                        description: r.description,
                      }));
                      setLineItems(isLoneBlank ? parsed : [...lineItems, ...parsed]);
                      setBulkText('');
                      setBulkOpen(false);
                      // Surface the cap when we actually truncated, so the broker
                      // knows the rest didn't make it (vs. silently dropping rows).
                      const noticeBase = `Added ${result.rows.length} line${result.rows.length === 1 ? '' : 's'} from paste. Review below and edit as needed.`;
                      setBulkNotice(
                        result.truncated
                          ? `${noticeBase} (Capped at ${result.rows.length} of ${result.totalParsed} pasted — RFQ max is 100 lines. Submit and create a follow-up RFQ for the remaining ${result.totalParsed - result.rows.length}.)`
                          : noticeBase,
                      );
                    }}
                    disabled={!bulkText.trim()}
                    className="px-3 py-1 text-xs font-medium bg-primary text-primary-foreground rounded disabled:opacity-50"
                  >
                    Add to lines
                  </button>
                </div>
              </div>
            )}
            {bulkNotice && !bulkOpen && (
              <div className="mb-3 text-xs text-primary border border-primary/30 bg-primary/5 rounded px-3 py-2 flex items-center justify-between gap-2">
                <span>{bulkNotice}</span>
                <button
                  type="button"
                  onClick={() => setBulkNotice(null)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            )}

            <div className="space-y-3">
              {lineItems.map((li, idx) => (
                <div
                  key={idx}
                  className="border border-border rounded p-3 bg-surface-muted/30 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-wide font-semibold text-foreground-muted">
                      Line {idx + 1}
                    </div>
                    {lineItems.length > 1 && (
                      <button
                        onClick={() =>
                          setLineItems(lineItems.filter((_, i) => i !== idx))
                        }
                        className="text-foreground-muted hover:text-danger"
                        aria-label="Remove line"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <label className="col-span-7 sm:col-span-5 block">
                      <div className="text-[11px] font-medium text-muted-foreground mb-1">
                        Part number *
                      </div>
                      <input
                        value={li.partNumber}
                        onChange={(e) =>
                          updateLine(setLineItems, lineItems, idx, { partNumber: e.target.value })
                        }
                        placeholder="L31809P11"
                        maxLength={80}
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                      />
                    </label>
                    <label className="col-span-5 sm:col-span-3 block">
                      <div className="text-[11px] font-medium text-muted-foreground mb-1">
                        Alt PN
                      </div>
                      <input
                        value={li.altPartNumber}
                        onChange={(e) =>
                          updateLine(setLineItems, lineItems, idx, { altPartNumber: e.target.value })
                        }
                        placeholder="L31809P09"
                        maxLength={80}
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                      />
                    </label>
                    <label className="col-span-12 sm:col-span-4 block">
                      <div className="text-[11px] font-medium text-muted-foreground mb-1">Qty *</div>
                      <input
                        type="number"
                        min={1}
                        max={100000}
                        value={li.qty}
                        onChange={(e) =>
                          updateLine(setLineItems, lineItems, idx, { qty: e.target.value })
                        }
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                      />
                    </label>
                  </div>
                  <label className="block">
                    <div className="text-[11px] font-medium text-muted-foreground mb-1">
                      Description
                    </div>
                    <input
                      value={li.description}
                      onChange={(e) =>
                        updateLine(setLineItems, lineItems, idx, { description: e.target.value })
                      }
                      placeholder="Nozzle, fuel, dual fuel"
                      maxLength={500}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                    />
                  </label>
                  <div className="grid grid-cols-12 gap-2">
                    <label className="col-span-12 sm:col-span-5 block">
                      <div className="text-[11px] font-medium text-muted-foreground mb-1">
                        Condition requested
                      </div>
                      <select
                        value={li.conditionRequested}
                        onChange={(e) =>
                          updateLine(setLineItems, lineItems, idx, {
                            conditionRequested: (e.target.value || '') as ConditionCode | '',
                          })
                        }
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                      >
                        <option value="">— any —</option>
                        {CONDITION_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="col-span-12 sm:col-span-5 block">
                      <div className="text-[11px] font-medium text-muted-foreground mb-1">
                        Cert requested
                      </div>
                      <select
                        value={li.certRequested}
                        onChange={(e) =>
                          updateLine(setLineItems, lineItems, idx, {
                            certRequested: (e.target.value || '') as CertType | '',
                          })
                        }
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                      >
                        <option value="">— not specified —</option>
                        {CERT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="col-span-12 sm:col-span-2 flex items-end gap-2 text-sm pb-1.5">
                      <input
                        type="checkbox"
                        checked={li.isAog}
                        onChange={(e) =>
                          updateLine(setLineItems, lineItems, idx, { isAog: e.target.checked })
                        }
                      />
                      AOG
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {createMutation.isError && (
            <div className="text-sm text-danger">
              {(createMutation.error as { message?: string })?.message ?? 'Save failed'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating…' : 'Create RFQ'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function updateLine(
  setter: (next: LineItemDraft[]) => void,
  current: LineItemDraft[],
  idx: number,
  patch: Partial<LineItemDraft>,
) {
  const next = current.slice();
  next[idx] = { ...next[idx]!, ...patch };
  setter(next);
}

// Compact-but-rich row for the customer search dropdown. Mirrors the
// /customers list row so brokers see the same shape of info in both
// places: company name + status pills, primary contact + phone + flag,
// then the RFQ-count / domain / country / last-RFQ-date footer plus
// lifetime paid total on the right.
function CustomerResultRow({
  c,
  onPick,
}: {
  c: CustomerListItem;
  onPick: () => void;
}) {
  const phoneInfo = phoneCountry(c.contactPhone);
  return (
    <button
      onClick={onPick}
      className="w-full text-left px-3 py-2.5 hover:bg-surface-muted border-b border-border last:border-b-0"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-semibold text-sm flex items-center gap-1.5 min-w-0">
          <span className="truncate">{c.displayName ?? c.legalName}</span>
          {c.hasAog && <span className="pill pill-warning text-[10px]">AOG×{c.aogCount}</span>}
          {c.isFaaPart145 && <span className="pill pill-info text-[10px]">FAA-145</span>}
          {c.isMilitary && <span className="pill pill-default text-[10px]">MIL</span>}
        </div>
        {c.lifetimePaidCents > 0 && (
          <div className="font-mono text-xs font-semibold text-success whitespace-nowrap">
            {fmtCompactUsd(c.lifetimePaidCents)}
          </div>
        )}
      </div>
      {(c.contactFullName || c.contactPhone || c.contactEmail) && (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground">
          {c.contactFullName && <span className="font-medium">{c.contactFullName}</span>}
          {c.contactPhone && (
            <span className="font-mono text-foreground-muted">
              {formatPhoneDisplay(c.contactPhone)}
            </span>
          )}
          {!c.contactFullName && !c.contactPhone && c.contactEmail && (
            <span className="text-foreground-muted truncate">{c.contactEmail}</span>
          )}
          {phoneInfo && (
            <span
              className="text-foreground-muted"
              title={phoneInfo.name}
              aria-label={phoneInfo.name}
            >
              {phoneInfo.flag} {phoneInfo.iso2}
            </span>
          )}
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-foreground-muted">
        <span>
          {c.lifetimeRfqs} RFQ{c.lifetimeRfqs === 1 ? '' : 's'}
        </span>
        {c.rfqs90d > 0 && <span>· {c.rfqs90d} in 90d</span>}
        {c.emailDomain && <span>· {c.emailDomain}</span>}
        {!phoneInfo && c.countryCode && <span>· {c.countryCode}</span>}
        {c.lastRfqAt && <span>· last RFQ {new Date(c.lastRfqAt).toLocaleDateString()}</span>}
      </div>
    </button>
  );
}

function SectionHeader({
  title,
  right,
}: {
  title: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-[11px] uppercase tracking-wide font-semibold text-foreground-muted">
        {title}
      </h3>
      {right}
    </div>
  );
}

function ToggleGroup<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="flex border border-border rounded overflow-hidden text-[11px]">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-2 py-1 ${
            value === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-background text-foreground-muted hover:bg-surface-muted'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
